import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import {
    EditOutlined as EditIcon,
    BlockOutlined as DeactivateIcon,
    Star as StarIcon,
    StarOutline as StarOutlineIcon,
    ViewModuleOutlined as TypeIcon,
    PlayCircleOutlineRounded as StartIcon,
    RestoreOutlined as ReactivateIcon
} from '@mui/icons-material';
import styles from './AppointmentTypeCard.module.css';
import { getModuleLabel } from '../../appointment/modules/appointmentModules';
import { getModuleIcon } from './moduleIcons';

const DENIED_EDIT = 'Apenas proprietários e administradores podem editar tipos de atendimento';
const DENIED_DEFAULT = 'Apenas proprietários e administradores podem definir o tipo padrão';
const DENIED_DEACTIVATE = 'Apenas proprietários e administradores podem desativar tipos de atendimento';
const DENIED_REACTIVATE = 'Apenas proprietários e administradores podem reativar tipos de atendimento';

/** Ação compacta do rodapé do card: ícone com tooltip; desligada mostra o motivo no tooltip. */
const CardAction = ({ title, deniedTitle, disabled, tone, onClick, children }) => (
    <Tooltip title={disabled ? deniedTitle : title} arrow>
        <span>
            <IconButton
                size="small"
                className={`${styles.action} ${tone ? styles[tone] : ''}`}
                onClick={onClick}
                disabled={disabled}
                aria-label={title}
            >
                {children}
            </IconButton>
        </span>
    </Tooltip>
);

/**
 * Card de um tipo de atendimento em Cadastros > Tipos de atendimento: nome, os módulos na
 * ordem em que aparecem na tela de atendimento e as ações. O padrão não pode
 * ser desativado: o usuário marca outro como padrão primeiro. Desativado ("Exibir
 * inativos") aparece apagado, com o selo Inativo e só a ação de reativar.
 */
const AppointmentTypeCard = ({ type, canManage = true, onEdit, onSetDefault, onDeactivate, onReactivate }) => {
    const inactive = type.Active === false;
    const modules = type.Modules || [];
    const moduleCount = modules.length;
    const subtitle = moduleCount === 0
        ? 'Nenhum módulo selecionado'
        : `${moduleCount} ${moduleCount === 1 ? 'módulo' : 'módulos'} nesta ordem`;

    return (
        <article className={`${styles.card} ${type.IsDefault ? styles.cardDefault : ''} ${inactive ? styles.cardInactive : ''}`}>
            <div className={styles.header}>
                <span className={styles.icon}>
                    <TypeIcon fontSize="small" />
                </span>
                <div className={styles.titleBlock}>
                    <h3 className={styles.name}>{type.Label || 'Tipo sem nome'}</h3>
                    <span className={styles.subtitle}>{subtitle}</span>
                </div>
                {type.IsDefault && (
                    <span className={styles.defaultTag}>
                        <StarIcon sx={{ fontSize: 14 }} />
                        Padrão
                    </span>
                )}
                {inactive && <span className={styles.inactiveTag}>Inativo</span>}
            </div>

            {moduleCount > 0 ? (
                <ol className={styles.modules}>
                    {modules.map((key, index) => {
                        const ModuleIcon = getModuleIcon(key);
                        return (
                            <li key={key} className={styles.module}>
                                <span className={styles.moduleIndex}>{index + 1}</span>
                                <ModuleIcon className={styles.moduleIcon} />
                                <span className={styles.moduleLabel}>{getModuleLabel(key)}</span>
                            </li>
                        );
                    })}
                </ol>
            ) : inactive ? null : (
                <Tooltip title={canManage ? '' : DENIED_EDIT} arrow>
                    <span className={styles.noModulesWrap}>
                        <button
                            type="button"
                            className={styles.noModules}
                            onClick={() => onEdit && onEdit(type)}
                            disabled={!canManage}
                        >
                            Escolher os módulos deste tipo
                        </button>
                    </span>
                </Tooltip>
            )}

            <div className={styles.footer}>
                <span className={styles.footerHint}>
                    {type.IsDefault && (
                        <>
                            <StartIcon className={styles.footerHintIcon} />
                            Sugerido ao iniciar um atendimento
                        </>
                    )}
                </span>
                <div className={styles.actions}>
                    {inactive && (
                        <CardAction title="Reativar tipo" deniedTitle={DENIED_REACTIVATE} disabled={!canManage} tone="restore" onClick={() => onReactivate && onReactivate(type)}>
                            <ReactivateIcon fontSize="small" />
                        </CardAction>
                    )}

                    {!inactive && !type.IsDefault && (
                        <CardAction title="Definir como padrão" deniedTitle={DENIED_DEFAULT} disabled={!canManage} onClick={() => onSetDefault && onSetDefault(type)}>
                            <StarOutlineIcon fontSize="small" />
                        </CardAction>
                    )}

                    {!inactive && (
                        <CardAction title="Editar tipo" deniedTitle={DENIED_EDIT} disabled={!canManage} onClick={() => onEdit && onEdit(type)}>
                            <EditIcon fontSize="small" />
                        </CardAction>
                    )}

                    {!inactive && !type.IsDefault && (
                        <CardAction title="Desativar tipo" deniedTitle={DENIED_DEACTIVATE} disabled={!canManage} tone="danger" onClick={() => onDeactivate && onDeactivate(type)}>
                            <DeactivateIcon fontSize="small" />
                        </CardAction>
                    )}
                </div>
            </div>
        </article>
    );
};

export default AppointmentTypeCard;
